import { useEffect, useRef } from "react";

const GOOGLE_CLIENT_ID =
  "1019081156563-jutv30d6a0k53t4rfph3hhf3mtve0kf4.apps.googleusercontent.com";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme: string;
              size: string;
              width?: number;
              text?: string;
            },
          ) => void;
        };
      };
    };
  }
}

interface GoogleSignInProps {
  onSuccess: (data: {
    status: string;
    token: string;
    refresh_token: string;
    [key: string]: unknown;
  }) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  // Firm name to send on first-time signup (Apple 3.1.3(c) B2B gate).
  // Server ignores it for returning users, so it's safe to always pass.
  firmName?: string;
}

export default function GoogleSignIn({
  onSuccess,
  onError,
  disabled,
  firmName,
}: GoogleSignInProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const firmNameRef = useRef(firmName);

  // Keep refs current so the Google callback always calls the latest version
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    firmNameRef.current = firmName;
  }, [firmName]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let attempts = 0;

    const handleCredentialResponse = async (response: {
      credential: string;
    }) => {
      try {
        const trimmedFirm = (firmNameRef.current ?? "").trim();
        const body: Record<string, string> = { id_token: response.credential };
        if (trimmedFirm) body.firm_name = trimmedFirm;
        const res = await fetch(
          "https://api.adjusterphoto.com/api/auth/google",
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-Auth-Mode": "cookie",
            },
            body: JSON.stringify(body),
          },
        );

        if (!res.ok) {
          const error = await res.json();
          onErrorRef.current(error.detail || "Authentication failed");
          return;
        }

        const data = await res.json();
        onSuccessRef.current(data);
      } catch (err) {
        onErrorRef.current("Network error. Please try again.");
      }
    };

    const initGoogle = () => {
      if (attempts++ > 50) {
        onErrorRef.current(
          "Google Sign-In failed to load. Please refresh the page.",
        );
        return;
      }

      if (!window.google || !buttonRef.current) {
        timeoutId = setTimeout(initGoogle, 100);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "outline",
        size: "large",
        width: 300,
        text: "signin_with",
      });
    };

    initGoogle();

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div
      ref={buttonRef}
      style={{
        display: "flex",
        justifyContent: "center",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    />
  );
}
